import { takeEvery, put, call, select, all } from "redux-saga/effects"

import {
  GET_RECOMMENDATIONS,
  GET_RECOMMENDATIONS_BY_CONTROL_ID,
  POST_RECOMMENDATION,
  PATCH_RECOMMENDATION,
  POST_OPINION_RECOMMENDATION,
  UPDATE_OPINIONS_VIEW_STATUS,
} from "./constants"

import {
  responseTypeIsError,
  responseTypeIsSuccess,
  manageErrorGenerator,
  manageSuccessGenerator,
} from "Containers/App/saga"

// Utils
import { getNewFilesNotification } from "./utils"
import { getRelatedFiles } from "ContainersForm/ETL/utils.js"
import request from "Utils/request"
import { config } from "Utils/config"
import { getCors, postCors, postFileCors, patchCors, deleteCors } from "Utils/rulesCors"
// Sagas
import { manageFilesETLsaga } from "ContainersForm/ETL/saga"
// Actions
import {
  getRecommendationsSucessAction,
  getRecommendationsByControlIdAction,
  getRecommendationsByControlIdSuccessAction,
  getRecommendationsAction,
} from "./actions"
import { postNotificationAction } from "Containers/Notifications/actions"
import { getControlsThatHaveRecommendationsAction } from "ContainersForm/ControlPlans/actions.js"
import { setErrorMessage } from "Containers/Messages/actions"
// Selectors
import { selectCurrentToken, selectCurrentUser } from "Containers/Login/selectors.js"
import { generateTemplateNotification } from "Components/Notifications/utils"
import { loadSagaTranslation } from "Utils/sagaTranslation"

function* getRecommendations() {
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = "recommendations"
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getRecommendationsSucessAction(response.body?.recommendations))
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* getRecommendationsByControlId({ payload }) {
  const { controlId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `recommendationsByControlId/${controlId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getRecommendationsByControlIdSuccessAction(response.body?.recommendations))
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* postRecommendation({ payload }) {
  const currentUser = yield select(selectCurrentUser)
  const trad = yield call(loadSagaTranslation, currentUser?.LANGUAGE_ID)
  const { recoToPost, filesToManage, controlId, filesNotifParams } = payload
  const hasNewFilesToPost = filesToManage?.filesToUploadETL?.length > 0
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `recommendations`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    if (filesToManage) {
      const previousNonConformityFiles = []
      const manageFilesResponse = yield call(manageFilesETLsaga, filesToManage)
      const { uploadedFiles, removedFiles } = manageFilesResponse
      recoToPost["relatedFiles"] = getRelatedFiles(
        previousNonConformityFiles,
        uploadedFiles,
        removedFiles
      )
    }
    const response = yield call(request, requestURL, postCors(recoToPost, specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: false })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: true })
        yield put(getRecommendationsByControlIdAction({ controlId }))
        yield put(getControlsThatHaveRecommendationsAction())
        yield put(getRecommendationsAction())
        if (hasNewFilesToPost) {
          const notifParams = { ...filesNotifParams, recommendationId: response?.body?.newId }
          const newFilesNotif = getNewFilesNotification(notifParams)
          const infos_notif = generateTemplateNotification("control_assignment_file", {
            notif: newFilesNotif,
            currentUser,
            payload: notifParams,
            trad,
          })
          yield put(postNotificationAction({ notif: newFilesNotif, infos_notif }))
        }
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* patchRecommendation({ payload }) {
  const currentUser = yield select(selectCurrentUser)
  const { recoId, controlId, recoToPatch, filesToManage, filesNotifParams } = payload
  const hasNewFilesToPost = filesToManage?.filesToUploadETL?.length > 0
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `recommendations/${recoId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    if (filesToManage) {
      const previousNonConformityFiles = recoToPatch?.relatedFiles
      const manageFilesResponse = yield call(manageFilesETLsaga, filesToManage)
      const { uploadedFiles, removedFiles } = manageFilesResponse
      recoToPatch["relatedFiles"] = getRelatedFiles(
        previousNonConformityFiles,
        uploadedFiles,
        removedFiles
      )
    }

    const response = yield call(request, requestURL, patchCors(recoToPatch, specificHeaders))

    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: false })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: true })
        // This is for refresh modal with recommendations and opinions
        yield put(getRecommendationsByControlIdAction({ controlId }))
        // This is for refresh table with controls who have recommendations and new icon new message
        yield put(getControlsThatHaveRecommendationsAction())
        // This if for refresh nav menus new recos or new messages
        yield put(getRecommendationsAction())
        if (hasNewFilesToPost) {
          const newFilesNotif = getNewFilesNotification(filesNotifParams)
          const infos_notif = generateTemplateNotification("control_assignment_file", {
            notif: newFilesNotif,
            currentUser,
            payload: filesNotifParams,
            trad,
          })
          yield put(postNotificationAction({ notif: newFilesNotif, infos_notif }))
        }
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* postOpinionRecommendation({ payload }) {
  const { opinionToPost, recommendationId, controlId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `addOpinionToRecommendation/${recommendationId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, patchCors({ opinionToPost }, specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        // This is for refresh modal with recommendations and opinions
        yield put(getRecommendationsByControlIdAction({ controlId }))
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* updateOpinionsViewStatusByRecoId({ payload }) {
  const { recommendationId, controlId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `updateOpinionsViewStatus/${recommendationId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, patchCors({}, specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        // This is for refresh modal with recommendations and opinions
        yield put(getRecommendationsByControlIdAction({ controlId }))
        // This is for refresh table with controls who have recommendations and new icon new message
        yield put(getControlsThatHaveRecommendationsAction())
        // This is for refresh counter in left menu
        yield put(getRecommendationsAction())
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* recommendationsSaga() {
  yield takeEvery(GET_RECOMMENDATIONS, getRecommendations)
  yield takeEvery(GET_RECOMMENDATIONS_BY_CONTROL_ID, getRecommendationsByControlId)
  yield takeEvery(POST_RECOMMENDATION, postRecommendation)
  yield takeEvery(PATCH_RECOMMENDATION, patchRecommendation)
  yield takeEvery(POST_OPINION_RECOMMENDATION, postOpinionRecommendation)
  yield takeEvery(UPDATE_OPINIONS_VIEW_STATUS, updateOpinionsViewStatusByRecoId)
}

export default recommendationsSaga
